import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DarkModeProvider } from './contexts/DarkModeContext';
import { AlertProvider, useAlert } from './contexts/AlertContext';
import { SearchProvider } from './contexts/SearchContext';
import Login from './components/Login';
import Layout from './components/Layout';
import Settings from './components/Settings';

// Password Manager modules
import PasswordManagerPasswords from './pages/apiKeyManager/Passwords';
import PasswordManagerTags from './pages/apiKeyManager/Tags';
import PasswordManagerLogs from './pages/apiKeyManager/Logs';

// Stalker modules
import StalkerPersons from './pages/stalker/Persons';
import StalkerPersonDetail from './pages/stalker/PersonDetail';
import StalkerNumbers from './pages/stalker/Numbers';
import StalkerEmails from './pages/stalker/Emails';
import StalkerLocations from './pages/stalker/Locations';
import StalkerSocials from './pages/stalker/Socials';
import StalkerSocialDetail from './pages/stalker/SocialDetail';
import StalkerMedias from './pages/stalker/Medias';
import StalkerLogs from './pages/stalker/Logs';
import StalkerSettings from './pages/stalker/Settings';

// Dashboard module
import Dashboard from './pages/dashboard/Dashboard';

// Notes module
import Notes from './pages/notes/Notes';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { isAuthenticated, loading } = useAuth();

	if (loading) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#E50914' }} />
			</div>
		);
	}
	return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

const TitleUpdater: React.FC = () => {
	const location = useLocation();

	useEffect(() => {
		let title = 'Manager';
		
		if (location.pathname === '/dashboard') {
			title = 'Dashboard';
		} else if (location.pathname === '/password-manager/passwords') {
			title = 'Passwords';
		} else if (location.pathname === '/password-manager/tags') {
			title = 'Tags';
		} else if (location.pathname === '/password-manager/logs') {
			title = 'Activity Logs';
		} else if (location.pathname.startsWith('/notes')) {
			title = 'Notes';
		} else if (location.pathname === '/stalker/persons') {
			title = 'Persons';
		} else if (location.pathname.startsWith('/stalker/persons/')) {
			title = 'Person Detail';
		} else if (location.pathname === '/stalker/numbers') {
			title = 'Numbers';
		} else if (location.pathname === '/stalker/emails') {
			title = 'Emails';
		} else if (location.pathname === '/stalker/locations') {
			title = 'Locations';
		} else if (location.pathname === '/stalker/socials') {
			title = 'Socials';
		} else if (location.pathname.startsWith('/stalker/socials/')) {
			title = 'Social Detail';
		} else if (location.pathname === '/stalker/medias') {
			title = 'Medias';
		} else if (location.pathname === '/stalker/logs') {
			title = 'Stalker Logs';
		} else if (location.pathname === '/stalker/settings') {
			title = 'Stalker Settings';
		} else if (location.pathname === '/settings') {
			title = 'Settings';
		}
		
		document.title = `${title} | Manager`;
	}, [location]);

	return null;
};

const AppContent: React.FC = () => {
	const { showAlert } = useAlert();

	useEffect(() => {
		const handleNetworkError = (event: any) => {
			showAlert('error', event.detail.message);
		};

		const handleServerError = (event: any) => {
			showAlert('error', event.detail.message);
		};

		window.addEventListener('networkError', handleNetworkError);
		window.addEventListener('serverError', handleServerError);

		return () => {
			window.removeEventListener('networkError', handleNetworkError);
			window.removeEventListener('serverError', handleServerError);
		};
	}, [showAlert]);

	return (
		<Router>
			<TitleUpdater />
			<Layout>
				<Routes>
					<Route path="/login" element={<Login />} />
					<Route path="/" element={<Navigate to="/dashboard" replace />} />
					<Route
						path="/dashboard"
						element={
							<ProtectedRoute>
								<Dashboard />
							</ProtectedRoute>
						}
					/>
					
					{/* Password Manager Routes */}
					<Route
						path="/password-manager/passwords"
						element={
							<ProtectedRoute>
								<PasswordManagerPasswords />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/password-manager/tags"
						element={
							<ProtectedRoute>
								<PasswordManagerTags />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/password-manager/logs"
						element={
							<ProtectedRoute>
								<PasswordManagerLogs />
							</ProtectedRoute>
						}
					/>

					{/* Stalker Routes */}
					<Route
						path="/stalker/persons"
						element={
							<ProtectedRoute>
								<StalkerPersons />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/stalker/persons/:id"
						element={
							<ProtectedRoute>
								<StalkerPersonDetail />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/stalker/numbers"
						element={
							<ProtectedRoute>
								<StalkerNumbers />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/stalker/emails"
						element={
							<ProtectedRoute>
								<StalkerEmails />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/stalker/locations"
						element={
							<ProtectedRoute>
								<StalkerLocations />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/stalker/socials"
						element={
							<ProtectedRoute>
								<StalkerSocials />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/stalker/socials/:platform/:id"
						element={
							<ProtectedRoute>
								<StalkerSocialDetail />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/stalker/medias"
						element={
							<ProtectedRoute>
								<StalkerMedias />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/stalker/logs"
						element={
							<ProtectedRoute>
								<StalkerLogs />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/stalker/settings"
						element={
							<ProtectedRoute>
								<StalkerSettings />
							</ProtectedRoute>
						}
					/>

					{/* Notes Routes */}
					<Route
						path="/notes"
						element={
							<ProtectedRoute>
								<Notes />
							</ProtectedRoute>
						}
					/>

					{/* Settings */}
					<Route
						path="/settings"
						element={
							<ProtectedRoute>
								<Settings />
							</ProtectedRoute>
						}
					/>

					{/* 404 Fallback */}
					<Route path="*" element={<Navigate to="/dashboard" replace />} />
				</Routes>
			</Layout>
		</Router>
	);
};

const App: React.FC = () => {
    return(
        <DarkModeProvider>
            <AuthProvider>
                <SearchProvider>
                    <AlertProvider>
                        <AppContent />
                    </AlertProvider>
                </SearchProvider>
            </AuthProvider>
        </DarkModeProvider>
    );
};

export default App;